var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useEffect, useRef, useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Keyboard, Pagination, Thumbs, Zoom, Controller } from 'swiper';
import { styled } from '@mui/system';
import { Sizes } from '../../types';
import { canUseDom, getAttributes, getColumnWidth, useDir, useResponsiveRender, useThemeUtils } from '../../../utils';
import { SliderThemes, SliderNavigationPositions, SliderNavigationButtons, SliderSpecificSlidesPerViewProps } from './types';
import { IconButton, IconButtonRole, IconButtonSize } from '../../atoms/icon-button';
import { Container, ContainerPaddings, ContainerDisplays, ContainerFlexJustifyContents, ContainerOverflows, ContainerWidths, ContainerFlexGaps, ContainerPositions, ContainerPositionSizes, ContainerFlexDirections, ContainerFlexAlignItems, ContainerHeights } from '../container';
import { Tabs, TabsThemes } from '../../molecules/tabs';
import { Text, TextVariants, TextHorizontalAlignments as TabsHorizontalAlignments } from '../../atoms/text';
var additonalSlides = function (slidesProps, propsForBreakpoints) {
    var newSlides = slidesProps;
    if (newSlides && propsForBreakpoints) {
        var slidesPerView = typeof propsForBreakpoints === 'number'
            ? 1
            : Object.keys(propsForBreakpoints).map(function (propsKey) { return propsForBreakpoints[propsKey].slidesPerView; });
        var condition = slidesPerView &&
            Array.isArray(slidesPerView) &&
            slidesPerView.some(function (el) { return el === undefined || el === SliderSpecificSlidesPerViewProps.Auto; })
            ? 4
            : slidesPerView;
        while (Math.max.apply(Math, [1].concat(condition)) * 3 > newSlides.length) {
            newSlides = newSlides.concat(newSlides);
        }
    }
    return newSlides;
};
var checkSliderConfig = function (slides) {
    var test = function (slide) {
        return slide &&
            Object.keys(slide).length > 0 &&
            Object.keys(slide).includes('slide');
    };
    return slides.every(test);
};
var mapTabsAndSlideThemes = function (slidesProps) {
    var individualSlideThemeArray = [];
    var tabsLabelArray = [];
    slidesProps.forEach(function (slide) {
        var setSlide = slide;
        individualSlideThemeArray.push(setSlide.slideTheme);
        if (setSlide.slideTabLabel !== undefined) {
            tabsLabelArray.push(setSlide.slideTabLabel);
            return;
        }
        tabsLabelArray.push('');
    });
    return { individualSlideThemeArray: individualSlideThemeArray, tabsLabelArray: tabsLabelArray };
};
var configureActiveNavigationButton = function (controllerActiveIndex, activeIndex, conditionValue) {
    if (controllerActiveIndex) {
        return controllerActiveIndex === conditionValue ? { disabled: true } : {};
    }
    return conditionValue === activeIndex ? { disabled: true } : {};
};
var MuiSliderContainer = styled(Container, {
    name: 'MuiSliderContainer',
    overridesResolver: function (wrapperProps, styles) { return [styles.root]; }
})({});
var S = function (_a) {
    var _b, _c, _d, _e, _f;
    var props = __rest(_a, []);
    var background = props.background, idThumbs = props.idThumbs, keyboard = props.keyboard, sliderPagination = props.sliderPagination, _g = props.autoHeight, autoHeight = _g === void 0 ? true : _g, className = props.className, lazyLoading = props.lazyLoading, centeredSlides = props.centeredSlides, navigationPosition = props.navigationPosition, navigationConfig = props.navigationConfig, accessibilityProps = props.accessibilityProps, sliderTheme = props.sliderTheme, slides = props.slides, loop = props.loop, sliderPadding = props.sliderPadding, numberOfColumns = props.numberOfColumns, sliderTabs = props.sliderTabs, propsPerBreakpoints = props.propsPerBreakpoints, _h = props.slideToClickedSlide, slideToClickedSlide = _h === void 0 ? true : _h, sliderWidths = props.sliderWidths, minHeightSlideWidth = props.minHeightSlideWidth, _j = props.verticalPadding, verticalPadding = _j === void 0 ? true : _j, onSliderInit = props.onSliderInit, thumbsConfig = props.thumbsConfig, sliderWrapperFlex = props.sliderWrapperFlex, onZoom = props.onZoom, zoom = props.zoom, controller = props.controller, slideOnClick = props.slideOnClick, zoomButtonAriaLabel = props.zoomButtonAriaLabel, _k = props.sliderOverflow, sliderOverflow = _k === void 0 ? true : _k, _l = props.sliderOverflowContainer, sliderOverflowContainer = _l === void 0 ? false : _l, rest = __rest(props, ["background", "idThumbs", "keyboard", "sliderPagination", "autoHeight", "className", "lazyLoading", "centeredSlides", "navigationPosition", "navigationConfig", "accessibilityProps", "sliderTheme", "slides", "loop", "sliderPadding", "numberOfColumns", "sliderTabs", "propsPerBreakpoints", "slideToClickedSlide", "sliderWidths", "minHeightSlideWidth", "verticalPadding", "onSliderInit", "thumbsConfig", "sliderWrapperFlex", "onZoom", "zoom", "controller", "slideOnClick", "zoomButtonAriaLabel", "sliderOverflow", "sliderOverflowContainer"]);
    var themeUtils = useThemeUtils();
    var isTabsAndThemesBySlideActive = slides && checkSliderConfig(slides);
    var slidesTabsAndThemesArray = isTabsAndThemesBySlideActive && mapTabsAndSlideThemes(slides);
    var defaultFirstSlideMessage = 'slider-first-slide';
    var defaultLastSlideMessage = 'slider-last-slide';
    var defaultPreviousSlideMessage = 'slider-previous-slide';
    var defaultNextSlideMessage = 'slider-next-slide';
    var _m = accessibilityProps || {}, _o = _m.firstSlideMessage, firstSlideMessage = _o === void 0 ? defaultFirstSlideMessage : _o, _p = _m.lastSlideMessage, lastSlideMessage = _p === void 0 ? defaultLastSlideMessage : _p, _q = _m.prevSlideMessage, prevSlideMessage = _q === void 0 ? defaultPreviousSlideMessage : _q, _r = _m.nextSlideMessage, nextSlideMessage = _r === void 0 ? defaultNextSlideMessage : _r;
    var a11y = {
        enabled: true,
        firstSlideMessage: firstSlideMessage,
        lastSlideMessage: lastSlideMessage,
        prevSlideMessage: prevSlideMessage,
        nextSlideMessage: nextSlideMessage
    };
    var swiperRef = useRef();
    var _s = useState(0), currentIndex = _s[0], setCurrentIndex = _s[1];
    var _t = useState(0), index = _t[0], setIndex = _t[1];
    var dir = useDir();
    var handleTabChange = function (event, newValue) {
        var _a;
        (_a = swiperRef.current) === null || _a === void 0 ? void 0 : _a.slideTo(newValue);
    };
    useEffect(function () {
        var _a;
        (_a = swiperRef === null || swiperRef === void 0 ? void 0 : swiperRef.current) === null || _a === void 0 ? void 0 : _a.changeLanguageDirection(dir);
    }, [dir]);
    var updateIndex = function (swiperInstance) {
        var _a;
        if (swiperInstance !== null) {
            var currentSlide = swiperInstance === null || swiperInstance === void 0 ? void 0 : swiperInstance.realIndex;
            (_a = zoom === null || zoom === void 0 ? void 0 : zoom.setShowZoomLabel) === null || _a === void 0 ? void 0 : _a.call(zoom, false);
            setCurrentIndex(currentSlide);
        }
    };
    var configSC = __assign(__assign(__assign(__assign({ width: ContainerWidths.Full }, (verticalPadding && {
        padding: themeUtils.getResponsiveProp([{ y: ContainerPaddings.Size_5 }])
    })), { background: background }), (sliderOverflowContainer
        ? { overflow: ContainerOverflows.Visible }
        : { overflow: ContainerOverflows.Hidden })), getAttributes(rest));
    var configSCb = __assign(__assign({ width: sliderWidths || ContainerWidths.Boxed, display: ContainerDisplays.Flex, flexJustifyContent: ContainerFlexJustifyContents.Center, flexDirection: ContainerFlexDirections.Column, flexAlignItems: ContainerFlexAlignItems.Center, background: background }, (navigationPosition === SliderNavigationPositions.Mid
        ? {
            position: ContainerPositions.Relative
        }
        : {})), ((zoom === null || zoom === void 0 ? void 0 : zoom.enabled)
        ? { className: 'slider-zoom-container', height: ContainerHeights.Full }
        : {}));
    // caution: config changes reflected in all Slider variants
    var configS = __assign(__assign(__assign(__assign(__assign(__assign({ onSwiper: onSliderInit, thumbs: { swiper: thumbsConfig === null || thumbsConfig === void 0 ? void 0 : thumbsConfig.sliderInstance } }, ((zoom === null || zoom === void 0 ? void 0 : zoom.enabled) ? { zoom: true } : {})), { controller: controller, keyboard: {
            enabled: keyboard
        } }), (sliderPagination && {
        pagination: {
            clickable: true
        }
    })), { watchSlidesProgress: true, loop: loop, autoHeight: autoHeight, observer: true, observeParents: true, className: [
            className,
            slidesTabsAndThemesArray &&
                "".concat((_b = slidesTabsAndThemesArray.individualSlideThemeArray[currentIndex]) === null || _b === void 0 ? void 0 : _b.toString().toLowerCase(), "-slide")
        ]
            .filter(Boolean)
            .join(' '), centeredSlides: centeredSlides, slideToClickedSlide: slideToClickedSlide, a11y: a11y, lazy: lazyLoading === null || lazyLoading === void 0 ? void 0 : lazyLoading.toString(), breakpoints: (propsPerBreakpoints &&
            themeUtils.getBreakpointsValues(propsPerBreakpoints)) ||
            themeUtils.getBreakpointsValues(themeUtils.getResponsiveProp([
                {
                    slidesPerView: 1,
                    spaceBetween: Number(themeUtils.getComputedSpacing(Number(Sizes.Size_3), true))
                },
                {
                    slidesPerView: 1,
                    spaceBetween: Number(themeUtils.getComputedSpacing(Number(Sizes.Size_3), true))
                },
                {
                    slidesPerView: 2,
                    spaceBetween: Number(themeUtils.getComputedSpacing(Number(Sizes.Size_5), true))
                }
            ])), modules: [Keyboard, Pagination, Thumbs, Zoom, Controller], initialSlide: currentIndex, onActiveIndexChange: updateIndex, onBeforeInit: function (swiper) {
            swiperRef.current = swiper;
        } }), (sliderOverflow
        ? { style: { overflow: ContainerOverflows.Visible } }
        : {})), (navigationPosition === SliderNavigationPositions.Mid
        ? { navigation: true }
        : {}));
    var slideAmount = (slides && slides.length) || 0;
    var themeCondition = isTabsAndThemesBySlideActive &&
        slidesTabsAndThemesArray &&
        [
            (_c = slidesTabsAndThemesArray.individualSlideThemeArray[currentIndex]) === null || _c === void 0 ? void 0 : _c.toString().toLowerCase(),
            "".concat((_d = slidesTabsAndThemesArray.individualSlideThemeArray[currentIndex]) === null || _d === void 0 ? void 0 : _d.toString().toLowerCase(), "-special")
        ].join(' ');
    var configIBprev = __assign(__assign({ iconButtonRole: IconButtonRole.Button, size: IconButtonSize.Medium, onClick: function () { var _a; return (_a = swiperRef.current) === null || _a === void 0 ? void 0 : _a.slidePrev(); }, icon: 'pmi-icon-chevronleft', 'aria-label': currentIndex === 0 ? firstSlideMessage : prevSlideMessage, className: [
            !themeCondition
                ? sliderTheme === SliderThemes.Dark && SliderThemes.Dark.toLowerCase()
                : themeCondition
        ].join(' ') }, ((navigationConfig === null || navigationConfig === void 0 ? void 0 : navigationConfig.buttons) === SliderNavigationButtons.Transparent
        ? {
            background: "".concat(SliderNavigationButtons.Transparent.toString().toLowerCase(), " !important"),
            size: IconButtonSize.Large
        }
        : {})), ((navigationConfig === null || navigationConfig === void 0 ? void 0 : navigationConfig.disabledButtons) &&
        configureActiveNavigationButton((_e = controller === null || controller === void 0 ? void 0 : controller.control) === null || _e === void 0 ? void 0 : _e.activeIndex, currentIndex, 0)));
    var configIBnext = __assign(__assign({ iconButtonRole: IconButtonRole.Button, size: IconButtonSize.Medium, onClick: function () { var _a; return (_a = swiperRef.current) === null || _a === void 0 ? void 0 : _a.slideNext(); }, icon: 'pmi-icon-chevronright', 'aria-label': slideAmount - 1 === currentIndex ? lastSlideMessage : nextSlideMessage, className: [
            !themeCondition
                ? sliderTheme === SliderThemes.Dark && SliderThemes.Dark.toLowerCase()
                : themeCondition
        ].join(' ') }, ((navigationConfig === null || navigationConfig === void 0 ? void 0 : navigationConfig.buttons) === SliderNavigationButtons.Transparent
        ? {
            background: "".concat(SliderNavigationButtons.Transparent.toString().toLowerCase(), " !important"),
            size: IconButtonSize.Large
        }
        : {})), ((navigationConfig === null || navigationConfig === void 0 ? void 0 : navigationConfig.disabledButtons) &&
        configureActiveNavigationButton((_f = controller === null || controller === void 0 ? void 0 : controller.control) === null || _f === void 0 ? void 0 : _f.activeIndex, currentIndex, ((slides === null || slides === void 0 ? void 0 : slides.length) || 0) - 1)));
    var configIBZoom = __assign(__assign(__assign({ iconButtonRole: IconButtonRole.Button, size: IconButtonSize.Medium, onClick: onZoom, icon: 'pmi-icon-fullscreen' }, (zoomButtonAriaLabel
        ? { 'aria-label': zoomButtonAriaLabel }
        : { 'aria-label': 'pmi-icon-fullscreen' })), { className: [
            !themeCondition
                ? sliderTheme === SliderThemes.Dark && SliderThemes.Dark.toLowerCase()
                : themeCondition
        ].join(' ') }), ((navigationConfig === null || navigationConfig === void 0 ? void 0 : navigationConfig.buttons) === SliderNavigationButtons.Transparent
        ? {
            background: "".concat(SliderNavigationButtons.Transparent.toString().toLowerCase(), " !important"),
            size: IconButtonSize.Large
        }
        : {}));
    // workaround only for product gallery thumbs
    if (thumbsConfig && canUseDom) {
        Array.from(document.querySelectorAll(".swiper-slide-".concat(thumbsConfig.thumbsContainerClass))).forEach(function (slide, indexValue) {
            slide.addEventListener('mouseover', function () {
                var _a;
                (_a = swiperRef.current) === null || _a === void 0 ? void 0 : _a.slideTo(indexValue);
            });
        });
    }
    var configBCtop = {
        display: ContainerDisplays.Flex,
        flexGap: ContainerFlexGaps.Size_3,
        width: ContainerWidths.Boxed,
        flexJustifyContent: ContainerFlexJustifyContents.End,
        padding: themeUtils.getResponsiveProp([
            { b: ContainerPaddings.Size_4, r: ContainerPaddings.Size_3 }
        ])
    };
    var configBCmid = __assign(__assign({ display: ContainerDisplays.Flex, className: 'buttons-container-mid', position: ContainerPositions.Absolute, flexJustifyContent: currentIndex !== 0
            ? ContainerFlexJustifyContents.SpaceBetween
            : ContainerFlexJustifyContents.End, width: (navigationConfig === null || navigationConfig === void 0 ? void 0 : navigationConfig.width) ||
            themeUtils.getResponsiveProp([
                getColumnWidth(59, 60),
                getColumnWidth(11, 12),
                getColumnWidth(42, 60)
            ]), zIndex: zoom ? 2 : 1 }, ((navigationConfig === null || navigationConfig === void 0 ? void 0 : navigationConfig.padding) ? { padding: navigationConfig.padding } : {})), ((navigationConfig === null || navigationConfig === void 0 ? void 0 : navigationConfig.disabledButtons) || loop
        ? { flexJustifyContent: ContainerFlexJustifyContents.SpaceBetween }
        : {}));
    var configBCZoom = {
        position: ContainerPositions.Absolute,
        flexJustifyContent: currentIndex !== 0
            ? ContainerFlexJustifyContents.SpaceBetween
            : ContainerFlexJustifyContents.End,
        positionCoordinates: themeUtils.getResponsiveProp([
            {
                bottom: ContainerPositionSizes.Size_5,
                right: ContainerPositionSizes.Size_5
            }
        ]),
        zIndex: 1
    };
    var configZoomLabelContainerWrapper = {
        display: ContainerDisplays.Flex,
        position: ContainerPositions.Absolute,
        flexJustifyContent: ContainerFlexJustifyContents.Center,
        width: ContainerWidths.Full,
        zIndex: 1
    };
    var configZoomLabelContainer = {
        className: 'zoom-label-container',
        padding: themeUtils.getResponsiveProp([
            {
                x: ContainerPaddings.Size_4,
                y: ContainerPaddings.Size_3
            }
        ]),
        onClick: slideOnClick,
        background: background
    };
    var zoomLabelProps = {
        content: zoom === null || zoom === void 0 ? void 0 : zoom.zoomLabel,
        variant: TextVariants.Body1
    };
    var configSW = __assign(__assign(__assign({ width: numberOfColumns ||
            themeUtils.getResponsiveProp([
                getColumnWidth(10, 12),
                getColumnWidth(10, 12),
                getColumnWidth(8, 12)
            ]) }, (sliderPadding || loop
        ? {
            padding: sliderPadding ||
                (loop &&
                    themeUtils.getResponsiveProp([
                        { l: ContainerPaddings.Size_0 }
                    ])) ||
                themeUtils.getResponsiveProp([
                    { l: ContainerPaddings.Size_5 },
                    { l: ContainerPaddings.Size_6 },
                    { l: ContainerPaddings.Size_0 }
                ])
        }
        : {})), (sliderWrapperFlex
        ? {
            display: ContainerDisplays.Flex,
            flexDirection: ContainerFlexDirections.Row
        }
        : {})), ((zoom === null || zoom === void 0 ? void 0 : zoom.enabled) ? { height: ContainerHeights.Full } : {}));
    var configSliderTabs = __assign({ value: currentIndex, horizontalAlignment: TabsHorizontalAlignments.Center, onChange: handleTabChange }, (sliderTabs &&
        slidesTabsAndThemesArray && __assign(__assign(__assign({}, sliderTabs), { tabLabels: slidesTabsAndThemesArray === null || slidesTabsAndThemesArray === void 0 ? void 0 : slidesTabsAndThemesArray.tabsLabelArray }), (sliderTheme === SliderThemes.Light
        ? { tabsTheme: TabsThemes.Light }
        : { tabsTheme: TabsThemes.Dark }))));
    var renderSlides = (loop &&
        slides &&
        additonalSlides(slides, propsPerBreakpoints
            ? themeUtils.getBreakpointsValues(propsPerBreakpoints)
            : 1)) ||
        slides;
    var midButtons = (React.createElement(Container, __assign({}, configBCmid),
        React.createElement(IconButton, __assign({}, configIBprev)),
        React.createElement(IconButton, __assign({}, configIBnext))));
    var topButtons = (React.createElement(Container, __assign({}, configBCtop),
        React.createElement(IconButton, __assign({}, configIBprev)),
        React.createElement(IconButton, __assign({}, configIBnext))));
    var containerConfig = {
        position: ContainerPositions.Absolute,
        width: ContainerWidths.Auto,
        height: '1px',
        padding: ContainerPaddings.None,
        overflow: ContainerOverflows.Hidden
    };
    var sliderLoopResponsiveRender = useResponsiveRender([
        midButtons,
        // eslint-disable-next-line react/jsx-no-useless-fragment
        React.createElement(React.Fragment, null)
    ]);
    var disableResponsiveRender = useResponsiveRender([null, midButtons]);
    var topNavResponsiveRender = useResponsiveRender([null, null, topButtons]);
    return (React.createElement(MuiSliderContainer, __assign({}, configSC),
        sliderTabs && React.createElement(Tabs, __assign({}, configSliderTabs)),
        React.createElement(Container, __assign({}, configSCb),
            navigationPosition === SliderNavigationPositions.Top &&
                topNavResponsiveRender,
            React.createElement(Container, __assign({}, configSW),
                React.createElement(Container, __assign({ "aria-live": "polite", "aria-atomic": "true" }, containerConfig), "".concat(currentIndex + 1, " / ").concat(renderSlides === null || renderSlides === void 0 ? void 0 : renderSlides.length)),
                React.createElement(Swiper, __assign({}, configS), renderSlides === null || renderSlides === void 0 ? void 0 : renderSlides.map(function (slide, i) {
                    var slideClickConfig = __assign(__assign({}, (index !== i
                        ? {
                            onClick: function (e) {
                                e.preventDefault();
                                setIndex(i);
                                return false;
                            }
                        }
                        : {})), (slideOnClick
                        ? {
                            onClick: slideOnClick
                        }
                        : {}));
                    if (slide &&
                        Object.keys(slide).length > 0 &&
                        Object.keys(slide).includes('activeSlide')) {
                        var configSlide_1 = slide;
                        var inactiveSlideContainerConfig = {
                            position: ContainerPositions.Absolute,
                            positionCoordinates: themeUtils.getResponsiveProp([
                                {
                                    left: ContainerPositionSizes.Size_0,
                                    right: ContainerPositionSizes.Size_0,
                                    top: ContainerPositionSizes.Size_0
                                }
                            ]),
                            className: i !== currentIndex ? 'visible-item' : 'invisible-item'
                        };
                        var activeSlideContainerConfig = {
                            className: i === currentIndex ? 'visible-item' : 'invisible-item'
                        };
                        return (React.createElement(SwiperSlide, __assign({}, slideClickConfig),
                            React.createElement(Container, __assign({}, inactiveSlideContainerConfig), configSlide_1.inactiveSlide),
                            React.createElement(Container, __assign({}, activeSlideContainerConfig), configSlide_1.activeSlide)));
                    }
                    if (isTabsAndThemesBySlideActive) {
                        var configSlide_2 = slide;
                        return React.createElement(SwiperSlide, { key: i }, configSlide_2.slide);
                    }
                    var configSlide = slide;
                    var slideStyleConfig = __assign(__assign({}, (!(zoom === null || zoom === void 0 ? void 0 : zoom.enabled)
                        ? { display: 'grid' }
                        : {
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        })), (minHeightSlideWidth ? { aspectRatio: '1 / 1' } : {}));
                    return (React.createElement(SwiperSlide, __assign({ style: slideStyleConfig, key: i }, slideClickConfig, (onSliderInit && idThumbs
                        ? { className: "swiper-slide-".concat(idThumbs) }
                        : {})), configSlide));
                }))),
            navigationPosition === SliderNavigationPositions.Mid &&
                !(navigationConfig === null || navigationConfig === void 0 ? void 0 : navigationConfig.disabledButtons) &&
                !loop && (React.createElement(Container, __assign({}, configBCmid),
                currentIndex !== 0 && React.createElement(IconButton, __assign({}, configIBprev)),
                currentIndex !== ((slides === null || slides === void 0 ? void 0 : slides.length) || 0) - 1 && (React.createElement(IconButton, __assign({}, configIBnext))))),
            navigationPosition === SliderNavigationPositions.Mid &&
                (navigationConfig === null || navigationConfig === void 0 ? void 0 : navigationConfig.disabledButtons) &&
                disableResponsiveRender,
            navigationPosition === SliderNavigationPositions.Mid &&
                loop &&
                sliderLoopResponsiveRender,
            (zoom === null || zoom === void 0 ? void 0 : zoom.showZoomLabel) && (zoom === null || zoom === void 0 ? void 0 : zoom.zoomLabel) && (React.createElement(Container, __assign({}, configZoomLabelContainerWrapper),
                React.createElement(Container, __assign({}, configZoomLabelContainer),
                    React.createElement(Text, __assign({}, zoomLabelProps))))),
            onZoom && (React.createElement(Container, __assign({}, configBCZoom),
                React.createElement(IconButton, __assign({}, configIBZoom)))))));
};
export { S as Slider };
